import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Instagram, ExternalLink } from "lucide-react";
import { useLang } from "../lib/LanguageContext";

/**
 * Renders a grid of Instagram posts using the official embed.
 * Admin pastes Instagram permalink URLs (e.g., https://www.instagram.com/p/ABC123/)
 * and we render <blockquote class="instagram-media"> + load the embed.js script.
 */
export default function InstagramFeed({ posts = [], handle = "" }) {
    const { lang } = useLang();
    const containerRef = useRef(null);

    useEffect(() => {
        if (!posts?.length) return;
        // Ensure Instagram's embed.js is loaded
        const SCRIPT_ID = "instagram-embed-script";
        if (!document.getElementById(SCRIPT_ID)) {
            const s = document.createElement("script");
            s.id = SCRIPT_ID;
            s.async = true;
            s.src = "https://www.instagram.com/embed.js";
            document.body.appendChild(s);
        } else if (window.instgrm?.Embeds) {
            window.instgrm.Embeds.process();
        }
        // After a tick (let blockquotes mount), re-process embeds
        const id = setTimeout(() => {
            if (window.instgrm?.Embeds) window.instgrm.Embeds.process();
        }, 600);
        return () => clearTimeout(id);
    }, [posts]);

    if (!posts?.length) return null;

    const heading = { al: "Na ndjek në Instagram", en: "Follow us on Instagram", it: "Seguici su Instagram", de: "Folge uns auf Instagram" }[lang] || "Follow us on Instagram";
    const sub = { al: "Pamje të reja nga jeta në Kompleksin Dea", en: "Fresh moments from life at Kompleksi Dea", it: "Momenti freschi dal Kompleksi Dea", de: "Frische Momente aus dem Kompleksi Dea" }[lang] || "";

    const igHandle = (handle || "").replace(/^https?:\/\/(www\.)?instagram\.com\//, "").replace(/\/$/, "").replace(/^@/, "");

    return (
        <section data-testid="instagram-feed" className="py-20 md:py-24 px-6 md:px-12 bg-[#FDFBF7]">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-12"
                >
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white mb-4">
                        <Instagram size={22} strokeWidth={1.5} />
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl text-[#0F3A59] tracking-tight">
                        {heading}
                    </h2>
                    {sub && <p className="text-[#1C1C1C]/65 font-light mt-2">{sub}</p>}
                    {igHandle && (
                        <a
                            href={`https://instagram.com/${igHandle}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="instagram-handle-link"
                            className="inline-flex items-center gap-1.5 mt-3 text-sm tracking-wide text-[#0F3A59] font-medium hover:text-[#CBA871] transition-colors"
                        >
                            @{igHandle}
                            <ExternalLink size={12} />
                        </a>
                    )}
                </motion.div>

                <div ref={containerRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {posts.map((url, i) => (
                        <motion.div
                            key={url + i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.06, duration: 0.4 }}
                            data-testid={`instagram-post-${i}`}
                            className="rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.05)] bg-white"
                        >
                            <blockquote
                                className="instagram-media"
                                data-instgrm-permalink={url}
                                data-instgrm-version="14"
                                style={{
                                    background: "#FFF",
                                    border: 0,
                                    margin: 0,
                                    minWidth: "260px",
                                    width: "100%",
                                }}
                            >
                                <a href={url} target="_blank" rel="noreferrer" className="block p-4 text-sm text-[#0F3A59]">
                                    {url}
                                </a>
                            </blockquote>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
